<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'dialogStoreType',
})
</script>
<template>
  <ant-Modal v-model:open="state.dialog.visible" transition-name="" :centered="true"
    :wrap-class-name="'wrap-shop-type-dialog'" :title="state.dialog.title" :width="state.dialog.width"
    :confirm-loading="state.loading.submit" :ok-text="state.dialog.submitTxt" @ok="onSubmit" @cancel="closeDialog"
    :closable="false" :maskClosable="false">
    <a-spin :spinning="state.loading.dialogUpload">
      <div class="form-content">
        <a-form ref="formRef" :model="state.ruleForm" :rules="state.rules" :wrapper-col="{ style: 'flex: 1' }">
          <a-form-item label="类型名称" name="typeName">
            <a-input v-model:value="state.ruleForm.typeName" placeholder="请输入类型名称" :maxlength="50" allow-clear />
          </a-form-item>
          <a-form-item label="类型分组" name="typeGroup">
            <a-select v-model:value="state.ruleForm.typeGroup" placeholder="请选择类型分组"
              :disabled="state.viewType === 'detail'" allow-clear>
              <a-select-option v-for="item in typeGroupList" :key="item.code" :value="item.code">{{ item.name
                }}</a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item label="排序" name="sortOrder">
            <div class="sort-input-number">
              <button class="sort-btn" :disabled="state.viewType === 'detail'" @click="onSortChange(-1)">−</button>
              <input class="sort-input" type="number" :value="state.ruleForm.sortOrder" :min="0" :max="9999"
                :disabled="state.viewType === 'detail'" placeholder="请输入排序" @input="onSortInput" />
              <button class="sort-btn" :disabled="state.viewType === 'detail'" @click="onSortChange(1)">+</button>
            </div>
          </a-form-item>
          <a-form-item label="状态" name="status">
            <a-radio-group v-model:value="state.ruleForm.status">
              <a-radio value="1">启用</a-radio>
              <a-radio value="0">禁用</a-radio>
            </a-radio-group>
          </a-form-item>
        </a-form>
      </div>
    </a-spin>
  </ant-Modal>
</template>

<script setup lang="ts" name="dialogStoreType">
import { reactive, ref, nextTick, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { cloneDeep } from 'lodash-es'
import { useStoreType } from '@/store/shop/storeType'
import { useDictionary } from '@/store/system/dictionary'

const storeTypeStore = useStoreType()
const dictionaryStore = useDictionary()

const emit = defineEmits(['createUpdate'])

const formRef = ref()

const INIT_RULE_FORM = {
  id: undefined as number | undefined,
  parentId: 0,
  typeGroup: undefined as string | undefined,
  typeName: '',
  sortOrder: 0,
  status: '1'
}

const state = reactive({
  dialog: {
    visible: false,
    title: '',
    submitTxt: '',
    width: '645px'
  },
  loading: {
    dialogUpload: false,
    submit: false
  },
  ruleForm: cloneDeep(INIT_RULE_FORM),
  rules: {
    typeName: [{ required: true, message: '请输入类型名称', trigger: 'blur' }],
    typeGroup: [{ required: true, message: '请选择类型分组', trigger: 'change' }],
    status: [{ required: true, message: '请选择状态', trigger: 'change' }]
  },
  viewType: ''
})

const typeGroupList = ref<any[]>([])

const getTypeGroupList = async () => {
  try {
    const data: any = await dictionaryStore.getDict({ code: 'store_type' })
    typeGroupList.value = data || []
  } catch (err) {
    console.error('获取类型分组字典失败', err)
    typeGroupList.value = []
  }
}

onMounted(() => {
  getTypeGroupList()
})

const openDialog = async (type: string, row?: any) => {
  state.viewType = type

  if (type === 'edit' && row) {
    state.dialog.title = '修改店铺类型'
    state.dialog.submitTxt = '修改'
  } else {
    state.dialog.title = '新增店铺类型'
    state.dialog.submitTxt = '确认'
  }

  state.dialog.visible = true

  nextTick(() => {
    if (type === 'edit' && row) {
      state.ruleForm = {
        id: row.id,
        parentId: row.parentId || 0,
        typeGroup: row.typeGroup || '',
        typeName: row.typeName || '',
        sortOrder: row.sortOrder || 0,
        status: String(row.status) || '1'
      }
    } else {
      state.ruleForm = cloneDeep(INIT_RULE_FORM)
      formRef.value?.resetFields()
    }
  })
}

const closeDialog = () => {
  state.dialog.visible = false
  state.loading.submit = false
  state.ruleForm = cloneDeep(INIT_RULE_FORM)
  formRef.value?.resetFields()
}

const onSortChange = (delta: number) => {
  let val = (state.ruleForm.sortOrder || 0) + delta
  if (val < 0) val = 0
  if (val > 9999) val = 9999
  state.ruleForm.sortOrder = val
}

const onSortInput = (e: Event) => {
  const val = parseInt((e.target as HTMLInputElement).value, 10)
  state.ruleForm.sortOrder = isNaN(val) ? (undefined as any) : val
}

const onSubmit = () => {
  formRef.value
    .validate()
    .then(async () => {
      state.loading.submit = true
      const param = cloneDeep(state.ruleForm)
      try {
        if (state.viewType === 'add') {
          await storeTypeStore.createStoreType(param)
        } else {
          await storeTypeStore.updateStoreType(param)
        }
        state.loading.submit = false
        emit('createUpdate')
      } catch {
        state.loading.submit = false
      }
    })
    .catch(() => {
      state.loading.submit = false
    })
}

defineExpose({
  openDialog,
  closeDialog
})
</script>

<style scoped lang="less">
.form-content {
  padding: 30px 36px 22px 36px;
}

:deep(.ant-form-item) {
  margin-bottom: 0px;
  padding-bottom: 24px;
}

:deep(.ant-form-item-label) {
  margin-left: 0px;
  margin-right: 5px;
  width: 80px;
  text-align: right;
  justify-content: flex-end;
  display: flex;
  align-items: center;
  height: 40px;
}

:deep(.ant-form-item-label > label) {
  font-size: 14px;
  line-height: 1;
  font-weight: 400;
}

:deep(.ant-input-affix-wrapper) {
  height: 40px;
  padding: 0 11px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}

:deep(.ant-input-disabled) {
  height: 40px;
  border-color: #C5C5D3;
  border-radius: 4px;
}

:deep(.ant-select-selector) {
  height: 40px !important;
  display: flex;
  align-items: center;
  border-radius: 4px;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(.ant-input-affix-wrapper:not(:hover):not(.ant-input-affix-wrapper-focused)) {
  border-color: #C5C5D3;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(.ant-select:not(.ant-select-disabled):not(.ant-select-focused) .ant-select-selector:not(:hover)) {
  border-color: #C5C5D3;
}

// 只覆盖默认态，hover/focus 保留 Ant Design 蓝色
:deep(textarea.ant-input:not(:hover):not(:focus)) {
  border-color: #C5C5D3;
}

:deep(.ant-input-number) {
  height: 40px;
  border-color: #C5C5D3;
  border-radius: 4px;
}

:deep(.ant-input-number-input) {
  height: 100%;
}

:deep(.ant-radio-wrapper) {
  height: 40px;
  display: flex;
  align-items: center;
}

.sort-input-number {
  display: flex;
  border: 1px solid #C5C5D3;
  border-radius: 4px;
  width: fit-content;

  .sort-btn {
    width: 40px;
    height: 40px;
    background: #fff;
    border: none;
    cursor: pointer;

    &:disabled {
      background: #f5f5f5;
      color: #bbb;
      cursor: not-allowed;
    }
  }

  .sort-input {
    width: 80px;
    height: 40px;
    border: none;
    border-left: 1px solid #C5C5D3;
    border-right: 1px solid #C5C5D3;
    text-align: center;
    outline: none;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:disabled {
      background: #f5f5f5;
      color: #bbb;
    }
  }
}

.space-divider {
  height: 1px;
  background: #A0B8E5;
}
</style>
